import React from 'react';
import {
  Layout,
  Breadcrumb,
  Card,
  Button,
  Row,
  Col,
  Typography,
  Space,
} from 'antd';
import { ProjectOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { emeraldColors } from '../../theme/emeraldTheme';

const { Content } = Layout;
const { Title, Text } = Typography;

interface BreadcrumbItem {
  label: string;
  path?: string;
}

interface StatisticItem {
  value: string | number;
  label: string;
  color?: string;
}

interface ActionButton {
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
  type?: 'primary' | 'default';
  style?: React.CSSProperties;
}

interface StandardPageLayoutProps {
  // 页面标题和副标题
  title: string;
  subtitle?: string;

  // 面包屑导航
  breadcrumbs: BreadcrumbItem[];

  // Banner统计数据
  statistics?: StatisticItem[];

  // Banner操作按钮
  actions?: ActionButton[];

  // 页面内容
  children: React.ReactNode;

  // 是否显示Banner
  showBanner?: boolean;

  // 自定义Banner内容
  customBannerContent?: React.ReactNode;

  // 页面图标
  icon?: React.ReactNode;

  // 功能标签
  tags?: string[];
}

const StandardPageLayout: React.FC<StandardPageLayoutProps> = ({
  title,
  subtitle,
  breadcrumbs,
  statistics = [],
  actions = [],
  children,
  showBanner = true,
  customBannerContent,
  icon,
  tags = [],
}) => {
  const navigate = useNavigate();

  // 渲染面包屑
  const renderBreadcrumbs = () => {
    if (!breadcrumbs || breadcrumbs.length === 0) return null;

    const breadcrumbItems = breadcrumbs.map((item, index) => ({
      key: index,
      title: item.path ? (
        <Button
          type="link"
          onClick={() => navigate(item.path!)}
          style={{
            padding: 0,
            fontSize: '14px',
            color: '#10b981',
            fontWeight: '500',
            height: 'auto',
            lineHeight: '1.4',
          }}
        >
          {item.label}
        </Button>
      ) : (
        <span style={{ fontSize: '14px', color: '#333', fontWeight: '600' }}>
          {item.label}
        </span>
      ),
    }));

    return (
      <div style={{ marginBottom: '12px', padding: '0 16px' }}>
        <Breadcrumb
          style={{ fontSize: '14px', color: '#666' }}
          separator={
            <span style={{ color: '#999', fontSize: '12px', margin: '0 8px' }}>
              /
            </span>
          }
          items={breadcrumbItems}
        />
      </div>
    );
  };

  // 渲染统一的统计卡片
  const renderStatistics = () => {
    if (statistics.length === 0) return null;

    return (
      <div
        style={{
          display: 'flex',
          gap: '20px',
          alignItems: 'center',
          width: '100%',
        }}
      >
        {statistics.map((stat, index) => (
          <div
            key={index}
            className="banner-statistic-item"
            style={{
              padding: '16px 20px',
              background: 'rgba(255, 255, 255, 0.12)',
              backdropFilter: 'blur(12px)',
              borderRadius: '12px',
              border: '1px solid rgba(255, 255, 255, 0.25)',
              textAlign: 'center',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
              minWidth: '100px',
              flex: 1,
            }}
          >
            <Text
              className="banner-statistic-value"
              style={{
                fontSize: '24px',
                fontWeight: '700',
                color: stat.color || '#fff',
                display: 'block',
                textShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
                lineHeight: '1.2',
              }}
            >
              {stat.value}
            </Text>
            <Text
              className="banner-statistic-label"
              style={{
                fontSize: '12px',
                color: 'rgba(255, 255, 255, 0.85)',
                fontWeight: '500',
                marginTop: '4px',
                display: 'block',
              }}
            >
              {stat.label}
            </Text>
          </div>
        ))}
      </div>
    );
  };

  // 渲染操作按钮
  const renderActions = () => {
    if (actions.length === 0) return null;

    return (
      <div style={{ display: 'flex', gap: '6px' }}>
        {actions.map((action, index) => (
          <Button
            key={index}
            className="banner-action-btn"
            type="primary"
            size="small"
            icon={action.icon}
            onClick={action.onClick}
            style={{
              background: 'rgba(255,255,255,0.15)',
              borderColor: 'rgba(255,255,255,0.3)',
              backdropFilter: 'blur(8px)',
              borderRadius: '6px',
              fontWeight: '500',
              fontSize: '11px',
              height: '26px',
              padding: '0 8px',
              ...action.style,
            }}
          >
            {action.label}
          </Button>
        ))}
      </div>
    );
  };

  // 渲染Banner - 项目详情风格
  const renderBanner = () => {
    if (!showBanner) return null;

    return (
      <Card
        style={{
          marginBottom: '16px',
          marginLeft: '16px',
          marginRight: '16px',
          borderRadius: '12px',
          overflow: 'hidden',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
          border: 'none',
        }}
        bodyStyle={{ padding: 0 }}
      >
        {/* 渐变背景头部 */}
        <div
          style={{
            background: `linear-gradient(135deg, 
              ${emeraldColors.primary[500]} 0%, 
              ${emeraldColors.primary[600]} 50%,
              ${emeraldColors.primary[700]} 100%
            )`,
            padding: '32px',
            color: 'white',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* 装饰性背景元素 */}
          <div
            style={{
              position: 'absolute',
              top: '-50px',
              right: '-50px',
              width: '200px',
              height: '200px',
              background:
                'radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%)',
              borderRadius: '50%',
              pointerEvents: 'none',
            }}
          />
          <div
            style={{
              position: 'absolute',
              bottom: '-30px',
              left: '-30px',
              width: '120px',
              height: '120px',
              background:
                'radial-gradient(circle, rgba(255, 255, 255, 0.08) 0%, transparent 70%)',
              borderRadius: '50%',
              pointerEvents: 'none',
            }}
          />

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              position: 'relative',
              zIndex: 1,
            }}
          >
            {/* 左侧标题区域 */}
            <div style={{ flex: 1 }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '12px',
                }}
              >
                <div
                  style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '12px',
                    background: 'rgba(255, 255, 255, 0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: '16px',
                    backdropFilter: 'blur(10px)',
                  }}
                >
                  {icon || (
                    <ProjectOutlined
                      style={{ fontSize: '24px', color: 'white' }}
                    />
                  )}
                </div>
                <div>
                  <h1
                    style={{
                      fontSize: '28px',
                      fontWeight: '600',
                      color: 'white',
                      margin: 0,
                      textShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                      lineHeight: '1.2',
                    }}
                  >
                    {title}
                  </h1>
                  {subtitle && (
                    <p
                      style={{
                        fontSize: '16px',
                        color: 'rgba(255, 255, 255, 0.9)',
                        margin: 0,
                        fontWeight: '500',
                        marginTop: '4px',
                      }}
                    >
                      {subtitle}
                    </p>
                  )}
                </div>
              </div>

              {/* 标签区域 */}
              {tags.length > 0 && (
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {tags.map((tag, index) => (
                    <span
                      key={index}
                      style={{
                        padding: '6px 12px',
                        background: 'rgba(255, 255, 255, 0.2)',
                        borderRadius: '20px',
                        fontSize: '13px',
                        color: 'white',
                        fontWeight: '500',
                        backdropFilter: 'blur(10px)',
                      }}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* 右侧操作按钮 */}
            {actions.length > 0 && (
              <div
                style={{
                  display: 'flex',
                  gap: '12px',
                  alignItems: 'flex-start',
                }}
              >
                {actions.map((action, index) => (
                  <Button
                    key={index}
                    type="primary"
                    icon={action.icon}
                    onClick={action.onClick}
                    style={{
                      background: 'rgba(255, 255, 255, 0.9)',
                      border: 'none',
                      color: emeraldColors.primary[600],
                      borderRadius: '8px',
                      height: '40px',
                      padding: '0 20px',
                      fontSize: '14px',
                      fontWeight: '600',
                      boxShadow: '0 4px 12px rgba(255, 255, 255, 0.3)',
                      ...action.style,
                    }}
                  >
                    {action.label}
                  </Button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* 白色背景统计区域 */}
        {statistics.length > 0 && (
          <div style={{ padding: '24px 32px' }}>
            <Row gutter={[24, 16]}>
              {statistics.map((stat, index) => (
                <Col
                  xs={12}
                  sm={
                    statistics.length <= 4
                      ? 6
                      : Math.floor(24 / statistics.length)
                  }
                  md={
                    statistics.length <= 4
                      ? 6
                      : Math.floor(24 / statistics.length)
                  }
                  lg={Math.floor(24 / statistics.length)}
                  key={index}
                >
                  <div style={{ textAlign: 'center' }}>
                    <div
                      style={{
                        fontSize: '32px',
                        fontWeight: '600',
                        color: stat.color || '#262626',
                        lineHeight: '1',
                        marginBottom: '8px',
                      }}
                    >
                      {stat.value}
                    </div>
                    <div
                      style={{
                        fontSize: '14px',
                        color: '#8c8c8c',
                        fontWeight: '500',
                      }}
                    >
                      {stat.label}
                    </div>
                    <div
                      style={{
                        width: '40px',
                        height: '3px',
                        background: stat.color || '#f0f0f0',
                        borderRadius: '2px',
                        margin: '8px auto 0',
                      }}
                    />
                  </div>
                </Col>
              ))}
            </Row>
          </div>
        )}

        {/* 自定义内容 */}
        {customBannerContent && (
          <div style={{ padding: '24px 32px' }}>{customBannerContent}</div>
        )}
      </Card>
    );
  };

  return (
    <div
      style={{
        background: '#f5f5f5',
        minHeight: '100vh',
        padding: '70px 0 0 0',
      }}
    >
      {/* 面包屑导航 */}
      {renderBreadcrumbs()}

      {/* Banner区域 */}
      {renderBanner()}

      {/* 主要内容区域 */}
      <div style={{ padding: '0 16px' }}>{children}</div>
    </div>
  );
};

export default StandardPageLayout;
